import EnlaceCta from '@/components/marketing/comun/EnlaceCta';
import IconoTrazo, { TRAZO_CHECK } from '@/components/marketing/comun/IconoTrazo';
import {
    INCLUIDO_EN_LOS_DOS,
    PLANES_INDIVIDUALES,
    SUFIJO_PRECIO,
    type LineaCuota,
    type PlanIndividual,
    type SimboloCuota,
} from '@/content/planesPrecios';

import {
    MICROCOPIA_PRUEBA,
    NOTA_QUE_CUENTA,
    TEXTO_ANTES_OCULTO,
    TEXTO_CTA_PRUEBA,
    TEXTO_TODAS_LAS_FUNCIONES,
    TITULO_INCLUIDO,
} from './textosSeccionPrecios';

// Trazos de 16×16 del lienzo para IconoTrazo (comun/IconoTrazo.tsx).
const TRAZOS_CUOTA: Record<SimboloCuota, string> = {
    microfono: 'M6 2h4v7a2 2 0 0 1-4 0z M3.5 8a4.5 4.5 0 0 0 9 0 M8 12.5v2',
    chat: 'M2.5 3.5h11v7h-6l-3 2.5v-2.5h-2z',
};

/** Importe en Outfit 52/44 que nunca se parte; el sufijo baja de línea si no cabe (UI-SPEC long-text). */
export function FilaPrecio({ precio }: { precio: string }) {
    return (
        <span className="flex flex-wrap items-baseline gap-2">
            <strong className="whitespace-nowrap font-display text-[44px] font-bold leading-none lg:text-[52px]">
                {precio}
            </strong>
            <span className="text-ds-300 font-semibold text-on-surface-variant">{SUFIJO_PRECIO}</span>
        </span>
    );
}

export function CabeceraPlan({ id, nombre, descripcion }: { id: string; nombre: string; descripcion: string }) {
    return (
        <div className="flex flex-col gap-1.5">
            <h3 id={id} className="m-0 font-display text-[26px] font-bold leading-[1.2] tracking-[-0.01em]">
                {nombre}
            </h3>
            <span className="text-ds-300 text-ink/90">{descripcion}</span>
        </div>
    );
}

export function CajaCuotas({ lineas, fondo }: { lineas: readonly LineaCuota[]; fondo: string }) {
    return (
        <ul className={`m-0 flex list-none flex-col gap-2.5 rounded-ds-lg border-2 border-ink px-4 py-3.5 ${fondo}`}>
            {lineas.map((linea) => (
                <li key={linea.simbolo} className="flex items-center gap-2.5 text-ds-400">
                    <IconoTrazo trazo={TRAZOS_CUOTA[linea.simbolo]} tamano={20} className="shrink-0 text-ink" />
                    <span>
                        <strong>{linea.destacado}</strong>
                        {linea.resto}
                    </span>
                </li>
            ))}
        </ul>
    );
}

export function TodasLasFunciones() {
    return (
        <span className="flex items-center gap-2 text-ds-300 font-semibold">
            <IconoTrazo trazo={TRAZO_CHECK} tamano={18} grosor={2} className="shrink-0 text-primary" />
            {TEXTO_TODAS_LAS_FUNCIONES}
        </span>
    );
}

function PiePrueba({ plan }: { plan: PlanIndividual }) {
    const destacado = plan.etiquetaDestacada !== null;
    return (
        <div className="mt-auto flex flex-col gap-2">
            <EnlaceCta
                href={plan.enlaceRegistro}
                variante={destacado ? 'inverso' : 'primario'}
                anchoCompleto
                className={destacado ? 'shadow-ds-acento-2' : undefined}
            >
                {TEXTO_CTA_PRUEBA}
            </EnlaceCta>
            <span className="text-center text-ds-100 text-on-surface-variant">{MICROCOPIA_PRUEBA}</span>
        </div>
    );
}

export function BloquePrecio({ plan }: { plan: PlanIndividual }) {
    return (
        <div className="flex flex-col justify-end gap-0.5 lg:min-h-[66px]">
            {plan.precioAnterior !== null && (
                <span className="text-ds-400 font-semibold text-on-surface-variant">
                    <s>{plan.precioAnterior}</s>
                    <span className="sr-only">{TEXTO_ANTES_OCULTO}</span>
                </span>
            )}
            <FilaPrecio precio={plan.precio} />
        </div>
    );
}

function CuerpoPlan({ plan, idTitulo, fondoCuotas }: { plan: PlanIndividual; idTitulo: string; fondoCuotas: string }) {
    return (
        <>
            <CabeceraPlan id={idTitulo} nombre={plan.nombre} descripcion={plan.descripcion} />
            <BloquePrecio plan={plan} />
            <CajaCuotas lineas={plan.lineasCuota} fondo={fondoCuotas} />
            <TodasLasFunciones />
            <PiePrueba plan={plan} />
        </>
    );
}

function TarjetaStarter({ plan, idTitulo }: { plan: PlanIndividual; idTitulo: string }) {
    return (
        <article
            aria-labelledby={idTitulo}
            className="flex flex-col gap-[18px] rounded-xl border-2 border-ink bg-surface-2 px-[18px] py-[22px] shadow-ds-2 lg:p-8"
        >
            <CuerpoPlan plan={plan} idTitulo={idTitulo} fondoCuotas="bg-surface-suave" />
        </article>
    );
}

function TarjetaDestacada({ plan, idTitulo }: { plan: PlanIndividual; idTitulo: string }) {
    return (
        <article
            aria-labelledby={idTitulo}
            className="flex flex-col overflow-hidden rounded-xl border-2 border-ink bg-tint-accent shadow-ds-3"
        >
            <div className="bg-ink px-4 py-2 text-center text-ds-100 font-bold tracking-[0.02em] text-canvas">
                {plan.etiquetaDestacada}
            </div>
            <div className="flex flex-1 flex-col gap-[18px] px-[18px] pb-[22px] pt-5 lg:px-8 lg:pb-8 lg:pt-7">
                <CuerpoPlan plan={plan} idTitulo={idTitulo} fondoCuotas="bg-surface-2" />
            </div>
        </article>
    );
}

function ElementoIncluidoItem({ titulo, texto }: { titulo: string; texto: string }) {
    return (
        <li className="flex items-start gap-2.5 text-ds-300 leading-[1.45]">
            <span className="mt-px flex size-[22px] shrink-0 items-center justify-center rounded-[7px] border-[1.5px] border-ink bg-accent-blue text-ink">
                <IconoTrazo trazo={TRAZO_CHECK} tamano={13} grosor={2.4} className="shrink-0" />
            </span>
            <span>
                <strong>{titulo}</strong> {texto}
            </span>
        </li>
    );
}

/** «Incluido en Starter y en Professional»: la lista sale de INCLUIDO_EN_LOS_DOS, que ya trae el texto C-1. */
export function PanelIncluido() {
    return (
        <div className="flex flex-col gap-[18px] rounded-xl border-2 border-ink bg-surface-2 px-[18px] py-[22px] lg:p-8">
            <h3 className="m-0 font-display text-ds-500 font-bold leading-[1.25] tracking-[-0.01em] lg:text-[22px]">
                {TITULO_INCLUIDO}
            </h3>
            <ul className="m-0 grid list-none grid-cols-1 gap-x-7 gap-y-3 p-0 lg:grid-cols-3">
                {INCLUIDO_EN_LOS_DOS.map((elemento) => (
                    <ElementoIncluidoItem key={elemento.titulo} titulo={elemento.titulo} texto={elemento.texto} />
                ))}
            </ul>
            <p className="m-0 rounded-[14px] border-2 border-dashed border-ink bg-surface-suave px-4 py-3.5 text-ds-300 leading-[1.55] text-ink/90">
                <strong className="text-ink">{NOTA_QUE_CUENTA.destacado}</strong>
                {NOTA_QUE_CUENTA.resto}
            </p>
        </div>
    );
}

/**
 * Starter, Professional y el panel «Incluido» (vista «Para ti»). Sin estado propio, pero es de
 * cliente: lo importa la isla SeccionPrecios (`use client`), así que viaja en su JavaScript.
 */
export default function PlanesIndividuales({ idBase }: { idBase: string }) {
    return (
        <div className="flex flex-col gap-7 lg:gap-10">
            <div className="grid grid-cols-1 items-stretch gap-5 lg:grid-cols-2 lg:gap-7">
                {PLANES_INDIVIDUALES.map((plan) =>
                    plan.etiquetaDestacada === null ? (
                        <TarjetaStarter key={plan.id} plan={plan} idTitulo={`${idBase}-${plan.id}`} />
                    ) : (
                        <TarjetaDestacada key={plan.id} plan={plan} idTitulo={`${idBase}-${plan.id}`} />
                    ),
                )}
            </div>
            <PanelIncluido />
        </div>
    );
}
